import { MetadataData } from "@metaplex-foundation/mpl-token-metadata";
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  Token,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { AnchorWallet } from "@solana/wallet-adapter-react";
import {
  AccountMeta,
  PublicKey,
  TransactionInstruction,
} from "@solana/web3.js";
import dayjs from "dayjs";
import { divide } from "mathjs";
import {
  MILLISECONDS_PER_SECOND,
  UNQ_PER_AMOUNT,
} from "../../../common/constants/common.constants";
import { chunk } from "loadsh";
import {
  AdditionalRewardPercentagePerYear,
  FixedRewardPercentagePerYear,
  NftStakingStatus,
  Rarity,
  StakePeriodType,
  StakePeriodTypeSolana,
  StakingStatus,
  StakingStatusSolana,
} from "../../../common/enums/staking.enum";
import { IDetailNftData } from "../../../common/interfaces/common.interface";
import {
  IMemberStakingInfo,
  IStakingAccountInfo,
} from "../../../common/interfaces/staking.interface";
import {
  METADATA_PROGRAM_ID,
  metadataSeed,
  mint,
  programFactory,
  RPC_CONNECTION,
  stakeRecordSeed,
  universeMetadataSeed,
  universeProgramFactory,
  unqClubSeed,
} from "../utilities";
import { getNftTokenAccountsByOwner } from "./nftClubs.helpers";

export const mapStakingStatus = (stakeStatus: number) => {
  switch (stakeStatus) {
    case 0:
      return StakingStatus.FundingInitiated;
    case 1:
      return StakingStatus.StakingInProgress;
    case 2:
      return StakingStatus.StakingPeriodOver;
    case 3:
      return StakingStatus.StakingCanceled;
    default:
      return StakingStatus.FundingInitiated;
  }
};

export const getStatusForStakingAccount = (
  memberRecord: IMemberStakingInfo | undefined,
  stakingAccount: IStakingAccountInfo
) => {
  const currentUnixTimestapm = divide(
    dayjs().valueOf(),
    MILLISECONDS_PER_SECOND
  );
  if (memberRecord && memberRecord.hasUnstaked && memberRecord.hasClaimed) {
    return StakingStatus.UnstakingFinished;
  }

  if (memberRecord && memberRecord.unstakePeriodEnd) {
    return currentUnixTimestapm > memberRecord.unstakePeriodEnd
      ? StakingStatus.UnstakingPeriodOver
      : StakingStatus.UnstakingInProgress;
  }
  if (
    stakingAccount.status === 1 &&
    stakingAccount.stakePeriodEnd &&
    currentUnixTimestapm >= stakingAccount.stakePeriodEnd
  ) {
    return StakingStatus.StakingPeriodOver;
  }
  return mapStakingStatus(stakingAccount.status);
};
export const getStatusForNftStaking = (stakingAccount: IStakingAccountInfo) => {
  const currentUnixTimestapm = divide(
    dayjs().valueOf(),
    MILLISECONDS_PER_SECOND
  );

  if (stakingAccount && stakingAccount.nftStakeRecord?.unstakePeriodEnd) {
    return currentUnixTimestapm >
      stakingAccount.nftStakeRecord?.unstakePeriodEnd
      ? NftStakingStatus.UnstakingPeriodOver
      : NftStakingStatus.UnstakingInProgress;
  }
};

export const getMembersStakingInfoFromProgram = async (
  wallet: AnchorWallet,
  stakeAddress: PublicKey
) => {
  try {
    const clubProgram = programFactory();
    const [stakeRecord] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        wallet.publicKey.toBuffer(),
        stakeRecordSeed,
        stakeAddress.toBuffer(),
      ],
      clubProgram.programId
    );

    const stakeRecordAccount = await clubProgram.account.stakeRecord.fetch(
      stakeRecord
    );
    const memberStakingInfo: IMemberStakingInfo = {
      address: stakeRecord,
      claimAmount: divide(
        stakeRecordAccount.claimAmount.toNumber(),
        UNQ_PER_AMOUNT
      ),
      depositAmount: divide(
        stakeRecordAccount.amount.toNumber(),
        UNQ_PER_AMOUNT
      ),
      hasClaimed: stakeRecordAccount.hasClaimed,
      hasUnstaked: stakeRecordAccount.hasUnstaked,
      stakeOwner: wallet.publicKey,
      unstakePeriodEnd: stakeRecordAccount.unstakePeriodEnd
        ? stakeRecordAccount.unstakePeriodEnd.toNumber()
        : undefined,
    };
    return memberStakingInfo;
  } catch (error) {
    console.log(error);
    return undefined;
  }
};

export const mapStakingStatusFromSolana = (
  stakingStatus: StakingStatusSolana
) => {
  switch (stakingStatus) {
    case StakingStatusSolana.Funding:
      return 0;
    case StakingStatusSolana.InProgress:
      return 1;
    case StakingStatusSolana.Finished:
      return 2;
    case StakingStatusSolana.Canceled:
      return 3;
    default:
      return 0;
  }
};

export const getUnqTokensAccountByMember = async (wallet: AnchorWallet) => {
  try {
    const tokenAccounts = await RPC_CONNECTION.getParsedTokenAccountsByOwner(
      wallet.publicKey,
      { mint: mint }
    );

    if (tokenAccounts.value.length === 0) {
      return;
    }
    tokenAccounts.value.sort((ta1, ta2) =>
      +ta2.account.data.parsed.info.tokenAmount.amount >
      +ta1.account.data.parsed.info.tokenAmount.amount
        ? 1
        : -1
    );
    return {
      amount:
        +tokenAccounts.value[0].account.data.parsed.info.tokenAmount.amount /
        UNQ_PER_AMOUNT,
      tokenAccountAddress: tokenAccounts.value[0].pubkey,
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const addCreateAtaIxAndGetAtaIfNeededForStakedNft = async (
  stakingAccount: IStakingAccountInfo,
  owner: PublicKey,
  wallet: AnchorWallet
): Promise<{
  tokenAccount: PublicKey | undefined;
  transactionIx: TransactionInstruction[];
}> => {
  let tokenAccount = stakingAccount.nftStakeRecord?.membersTokenAccount;
  const transactionIx: TransactionInstruction[] = [];
  if (stakingAccount.nftStakeRecord) {
    const tokenAccounts = await RPC_CONNECTION.getParsedTokenAccountsByOwner(
      owner,
      { mint: stakingAccount.nftStakeRecord.mint }
    );

    const existingAccount = tokenAccounts.value.find(
      (item) =>
        item.pubkey.toString() ===
        stakingAccount.nftStakeRecord?.membersTokenAccount.toString()
    );

    if (!existingAccount) {
      if (tokenAccounts.value.length > 0) {
        tokenAccount = tokenAccounts.value[0].pubkey;
        return {
          tokenAccount,
          transactionIx,
        };
      }
      tokenAccount = await Token.getAssociatedTokenAddress(
        ASSOCIATED_TOKEN_PROGRAM_ID, // always ASSOCIATED_TOKEN_PROGRAM_ID
        TOKEN_PROGRAM_ID, // always TOKEN_PROGRAM_ID
        stakingAccount.nftStakeRecord.mint, // mint
        owner, // owner
        false
      );
      transactionIx.push(
        Token.createAssociatedTokenAccountInstruction(
          ASSOCIATED_TOKEN_PROGRAM_ID, // always ASSOCIATED_TOKEN_PROGRAM_ID
          TOKEN_PROGRAM_ID, // always TOKEN_PROGRAM_ID
          stakingAccount.nftStakeRecord.mint, // mint
          tokenAccount, // ata
          owner, // owner of token account
          wallet.publicKey // fee payer
        )
      );
    }
  }

  return {
    tokenAccount,
    transactionIx,
  };
};

export const getAdditionalReward = (rarity: Rarity) => {
  switch (rarity) {
    case Rarity.Common:
      return AdditionalRewardPercentagePerYear.Common;
    case Rarity.Epic:
      return AdditionalRewardPercentagePerYear.Epic;
    case Rarity.Legendary:
      return AdditionalRewardPercentagePerYear.Legendary;
    case Rarity.Rare:
      return AdditionalRewardPercentagePerYear.Rare;
  }
};

export const getFixedRewardPercentage = (
  stakePeriodType: StakePeriodTypeSolana
) => {
  switch (stakePeriodType) {
    case StakePeriodTypeSolana.OneMonth:
      return FixedRewardPercentagePerYear.OneMonth;
    case StakePeriodTypeSolana.SixMonths:
      return FixedRewardPercentagePerYear.SixMonths;
    case StakePeriodTypeSolana.ThreeMonths:
      return FixedRewardPercentagePerYear.ThreeMonths;
    case StakePeriodTypeSolana.TwelveMonths:
      return FixedRewardPercentagePerYear.TwelveMonths;
  }
};

export const getStakingPeriodType = (
  stakePeriodType: StakePeriodTypeSolana
) => {
  switch (stakePeriodType) {
    case StakePeriodTypeSolana.OneMonth:
      return StakePeriodType.OneMonth;
    case StakePeriodTypeSolana.SixMonths:
      return StakePeriodType.SixMonths;
    case StakePeriodTypeSolana.ThreeMonths:
      return StakePeriodType.ThreeMonths;
    case StakePeriodTypeSolana.TwelveMonths:
      return StakePeriodType.TwelveMonths;
  }
};

export const getRemainingAccountsForFinishStaking = (
  stakingAccount: IStakingAccountInfo,
  membersTokenAccount: PublicKey | undefined
) => {
  const remainingAccounts: AccountMeta[] = [];
  if (stakingAccount.nftStakeRecord) {
    remainingAccounts.push(
      {
        pubkey: stakingAccount.nftStakeRecord.stakedNftTokenAccount,
        isSigner: false,
        isWritable: true,
      },
      {
        pubkey:
          membersTokenAccount ??
          stakingAccount.nftStakeRecord.membersTokenAccount,
        isSigner: false,
        isWritable: true,
      },
      {
        pubkey: stakingAccount.nftStakeRecord.nftOwner,
        isSigner: false,
        isWritable: true,
      }
    );
  }
  return remainingAccounts;
};

export const getUnqLevel1001Nfts = async (wallet: AnchorWallet) => {
  try {
    const nftTokenAccounts = await getNftTokenAccountsByOwner(wallet.publicKey);
    const universeProgram = universeProgramFactory();
    const unqNfts: IDetailNftData[] = [];
    const metadataArray: PublicKey[] = [];
    const universeMetadataArray: PublicKey[] = [];
    const metadataAccounts: any[] = [];

    for (const nft of nftTokenAccounts) {
      try {
        const [metadataAddress] = await PublicKey.findProgramAddress(
          [
            metadataSeed,
            METADATA_PROGRAM_ID.toBuffer(),
            new PublicKey(nft.account.data.parsed.info.mint).toBuffer(),
          ],
          METADATA_PROGRAM_ID
        );

        const [universeMetadata] = await PublicKey.findProgramAddress(
          [
            metadataSeed,
            universeProgram.programId.toBuffer(),
            new PublicKey(nft.account.data.parsed.info.mint).toBuffer(),
            universeMetadataSeed,
          ],
          universeProgram.programId
        );

        metadataArray.push(metadataAddress);
        universeMetadataArray.push(universeMetadata);
      } catch (error) {
        console.log(error);
      }
    }

    const metadataAccountsPubkeysChuncked = chunk(metadataArray, 100);
    for (const metadataAccountsPubkeys of metadataAccountsPubkeysChuncked) {
      const metadataAccountsRaw = await RPC_CONNECTION.getMultipleAccountsInfo(
        metadataAccountsPubkeys
      );

      for (const metadataAccRaw of metadataAccountsRaw) {
        if (metadataAccRaw?.data) {
          metadataAccounts.push(MetadataData.deserialize(metadataAccRaw?.data));
        }
      }
    }

    const universeAccounts: any[] = (
      await universeProgram.account.universeMetadata.fetchMultiple(
        universeMetadataArray
      )
    ).filter((item) => item !== null && item !== undefined);

    for (const metadataAccount of metadataAccounts) {
      const universeMetadataAccount = universeAccounts.find(
        (item) => item.mint.toString() === metadataAccount.mint.toString()
      );
      if (
        !universeMetadataAccount ||
        universeMetadataAccount.currentUniverseLevel !== 1001
      ) {
        continue;
      }

      const tokenAccount = nftTokenAccounts.find(
        (item) => item.account.data.parsed.info.mint === metadataAccount.mint
      );

      if (!tokenAccount) {
        continue;
      }

      const nftJson = await (await fetch(metadataAccount.data.uri)).json();

      const [universeMetadata] = await PublicKey.findProgramAddress(
        [
          metadataSeed,
          universeProgram.programId.toBuffer(),
          new PublicKey(metadataAccount.mint).toBuffer(),
          universeMetadataSeed,
        ],
        universeProgram.programId
      );
      unqNfts.push({
        collection: nftJson.collection
          ? {
              family: nftJson.collection.family,
              name: nftJson.collection.name,
            }
          : undefined,
        mint: new PublicKey(metadataAccount.mint),
        name: metadataAccount.data.name,
        tokenAccount: tokenAccount.pubkey,
        nftUrl: nftJson.image,
        universeRarity: nftJson.attributes.find(
          (item) => item.trait_type === "Rule rarity"
        ).value as Rarity,
        universeMetaData: universeMetadata,
      });
    }

    return unqNfts;
  } catch (error) {
    console.log(error);
    throw error;
  }
};
